import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  TooltipProps,
} from 'recharts'

import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
// TODO: Migrate to V2
import { Colors } from '../../../styles/theme'

type PayloadData = {
  name: NameType
  value: ValueType
}

type CustomTooltipProps = TooltipProps<ValueType, NameType> & {
  active?: boolean
  payload?: PayloadData[]
  label?: string
}
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  const formatTooltip = (value: number, name: string) => {
    const displayValue = name === 'Expenses' ? Math.abs(value) : value
    const formattedValue = formatCurrencyFromCents(displayValue)
    return [formattedValue, name]
  }

  if (active && payload && payload.length) {
    return (
      <div className="bg-neutral-0 border border-neutral-200 p-2.5 rounded text-sm">
        <p className="text-neutral-700 font-bold">{label}</p>
        {payload.map((entry, index) => {
          if (typeof entry.name !== 'string') {
            return (
              <p key={index} className="text-neutral-700 my-2">
                Unknown: N/A
              </p>
            )
          }
          const color =
            entry.name === 'Income'
              ? 'text-green-700'
              : entry.name === 'Expenses'
                ? 'text-yellow-700'
                : 'text-neutral-700'
          if (typeof entry.value !== 'number') {
            return (
              <p key={index} className={`my-1 ${color}`}>
                {`${entry.name}: N/A`}
              </p>
            )
          }
          return (
            <p key={index} className={`my-1 ${color}`}>
              {`${entry.name}: ${formatTooltip(entry.value, entry.name)[0]}`}
            </p>
          )
        })}
      </div>
    )
  }
  return null
}

export const InsightsTrendGraph = ({
  data,
}: {
  data: {
    [key: string]: string | number | undefined
  }[]
}) => {
  const formatYAxis = (value: number) => {
    return formatCurrencyFromCents(value, { hideDecimalsIfZero: true })
  }

  return (
    <div className="max-w-[1200px]">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={data} stackOffset="sign">
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12, fill: '#6E6E6D' }}
          />
          <YAxis
            tickFormatter={formatYAxis}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12, fill: '#6E6E6D' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            align="center"
            layout="horizontal"
            wrapperStyle={{ paddingTop: 24 }}
            formatter={(value) => (
              <span className="text-xs text-neutral-700">{value}</span>
            )}
          />
          <Bar
            dataKey="total_income"
            name="Income"
            fill={Colors.greenBlue}
            stackId="a"
            barSize={30}
            legendType="square"
          />
          <Bar
            dataKey="total_expenses"
            name="Expenses"
            fill={Colors.yellowOrange}
            stackId="a"
            barSize={30}
            legendType="square"
          />
          <Line
            type="linear"
            dataKey="profit"
            name="Profit"
            stroke="#656A47"
            strokeWidth={3}
            dot={false}
            legendType="plainline"
          />
          <ReferenceLine y={0} stroke={Colors.black} strokeWidth={1} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
